import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Smartphone, CheckCircle2, ArrowRight, Wifi, WifiOff, Download, Bell, Zap } from 'lucide-react';

const PWA = () => {
  const benefits = [
    { icon: WifiOff, title: 'Offline-Funktionalität', desc: 'Ihre App funktioniert auch ohne Internetverbindung' },
    { icon: Download, title: 'Installierbar', desc: 'Direkt auf dem Homescreen ohne App Store' },
    { icon: Bell, title: 'Push-Nachrichten', desc: 'Erreichen Sie Nutzer mit gezielten Benachrichtigungen' },
    { icon: Zap, title: 'Blitzschnell', desc: 'Native App-Geschwindigkeit im Browser' },
  ];

  const comparison = [
    { feature: 'Installierbar', pwa: true, website: false, native: true },
    { feature: 'Offline-fähig', pwa: true, website: false, native: true },
    { feature: 'Push-Nachrichten', pwa: true, website: false, native: true },
    { feature: 'Kein App Store nötig', pwa: true, website: true, native: false },
    { feature: 'Ein Code für alle Plattformen', pwa: true, website: true, native: false },
    { feature: 'Günstigere Entwicklung', pwa: true, website: true, native: false },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-900 via-violet-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-violet-500/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-violet-500/10 border border-violet-500/20 text-violet-300 text-sm font-medium mb-6">
              <Smartphone size={16} />
              PWA Entwicklung
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Progressive Web Apps
            </h1>
            <p className="text-xl text-slate-300 leading-relaxed mb-8">
              Das Beste aus Web und nativen Apps vereint. Installierbar, offline-fähig und blitzschnell.
            </p>
            <Link to="/kontakt">
              <Button size="lg" className="bg-violet-600 hover:bg-violet-700 text-white">
                PWA entwickeln lassen
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-slate-900">Vorteile einer PWA</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, i) => (
              <Card key={i} className="border-0 shadow-lg">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-xl bg-violet-100 flex items-center justify-center mx-auto mb-4">
                    <b.icon className="text-violet-600" size={28} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{b.title}</h3>
                  <p className="text-sm text-slate-600">{b.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900">PWA vs. Website vs. Native App</h2>
          </div>
          <Card className="border-0 shadow-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Feature</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-violet-600">PWA</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">Website</th>
                    <th className="px-6 py-4 text-center text-sm font-semibold text-slate-600">Native App</th>
                  </tr>
                </thead>
                <tbody>
                  {comparison.map((row, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="px-6 py-4 text-sm text-slate-700">{row.feature}</td>
                      <td className="px-6 py-4 text-center">{row.pwa ? <CheckCircle2 className="text-green-500 mx-auto" size={20} /> : <span className="text-slate-300">—</span>}</td>
                      <td className="px-6 py-4 text-center">{row.website ? <CheckCircle2 className="text-green-500 mx-auto" size={20} /> : <span className="text-slate-300">—</span>}</td>
                      <td className="px-6 py-4 text-center">{row.native ? <CheckCircle2 className="text-green-500 mx-auto" size={20} /> : <span className="text-slate-300">—</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-violet-600">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Bereit für Ihre PWA?</h2>
          <p className="text-violet-100 mb-8">PWA-Entwicklung ab 2.990€ oder Konvertierung Ihrer bestehenden Website.</p>
          <Link to="/kontakt">
            <Button size="lg" className="bg-white text-violet-600 hover:bg-violet-50">
              Jetzt beraten lassen
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PWA;
